<template>
  <div class="homework-correction">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">作业批改</h1>
          <div class="header-shortcuts">
            <div class="shortcut-item" @click="navigateTo('/student/error-book')">
              <i class="fa fa-book"></i>
              <span>错题本</span>
            </div>
          </div>
        </div>
        <div class="header-tip">
          <i class="fa fa-lightbulb-o"></i>
          对台灯说「批改作业」，拍照后批改结果会出现在这里
        </div>
      </div>
    </div>

    <!-- 筛选栏 -->
    <div class="filter-bar">
      <div class="filter-left">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          @change="handleFilterChange">
        </el-date-picker>
      </div>
      <div class="filter-right">
        <el-button size="small" icon="el-icon-refresh" @click="loadRecords">刷新</el-button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-text">
        <i class="el-icon-loading"></i>
        正在加载批改记录...
      </div>
    </div>

    <!-- 空数据 -->
    <div v-else-if="!recordList.length" class="empty-container">
      <div class="empty-text">暂无批改记录</div>
    </div>

    <!-- 批改记录列表 -->
    <div v-else class="record-grid">
      <div
        v-for="record in recordList"
        :key="record.id"
        class="record-card"
        @click="openDetail(record)"
      >
        <div class="record-image">
          <img v-if="recordImage(record)" :src="recordImage(record)" alt="批改结果" />
          <div v-else class="image-placeholder">
            <i class="fa fa-image"></i>
            <span>暂无批改图</span>
          </div>
        </div>

        <div class="record-info">
          <div class="record-time">{{ formatTime(record.createTime) }}</div>
          <div class="record-counts">
            <span class="count-total">共 {{ record.questionCount || 0 }} 题</span>
            <span class="count-correct">对 {{ record.correctCount || 0 }}</span>
            <span class="count-wrong">错 {{ record.wrongCount || 0 }}</span>
          </div>
          <el-progress
            :percentage="recordRate(record)"
            :stroke-width="8"
            :color="rateColor(recordRate(record))">
          </el-progress>
        </div>

        <div class="record-actions">
          <el-button size="mini" type="text" @click.stop="openDetail(record)">
            <i class="fa fa-eye"></i> 查看详情
          </el-button>
          <el-button size="mini" type="text" class="text-danger" @click.stop="removeRecord(record)">
            <i class="fa fa-trash-o"></i> 删除
          </el-button>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div v-if="!loading && total > 0" class="pagination-wrapper">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[12, 24, 48]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total">
      </el-pagination>
    </div>

    <!-- 批改详情 -->
    <el-dialog
      title="批改详情"
      :visible.sync="detailVisible"
      width="1000px"
      center>
      <div v-if="detailLoading" class="detail-loading">
        <i class="el-icon-loading"></i> 正在加载...
      </div>
      <div v-else-if="currentRecord" class="detail-body">
        <div class="detail-left">
          <img
            v-if="recordImage(currentRecord)"
            :src="recordImage(currentRecord)"
            class="detail-image"
            @click="viewImage(recordImage(currentRecord))" />
          <div v-else class="image-placeholder large">
            <i class="fa fa-image"></i>
            <span>暂无批改图</span>
          </div>
          <div class="detail-meta">
            <div>批改时间：{{ formatTime(currentRecord.createTime) }}</div>
            <div>
              共 {{ currentRecord.questionCount || 0 }} 题 ·
              正确 {{ currentRecord.correctCount || 0 }} 题 ·
              正确率 {{ recordRate(currentRecord) }}%
            </div>
          </div>
        </div>

        <div class="detail-right">
          <div class="question-toolbar">
            <el-radio-group v-model="questionFilter" size="mini">
              <el-radio-button label="all">全部</el-radio-button>
              <el-radio-button label="wrong">错题</el-radio-button>
              <el-radio-button label="correct">正确</el-radio-button>
            </el-radio-group>
            <el-button
              size="mini"
              type="primary"
              :disabled="!selectedQuestionIds.length"
              @click="batchPrint">
              打印选中（{{ selectedQuestionIds.length }}）
            </el-button>
          </div>

          <div v-if="!filteredQuestions.length" class="empty-text small">暂无题目</div>
          <el-checkbox-group v-else v-model="selectedQuestionIds" class="question-list">
            <div
              v-for="question in filteredQuestions"
              :key="question.id"
              class="question-item"
              :class="{ wrong: !question.isCorrect }">
              <div class="question-head">
                <el-checkbox :label="question.id"><span></span></el-checkbox>
                <el-tag :type="question.isCorrect ? 'success' : 'danger'" size="mini">
                  {{ question.isCorrect ? '正确' : '错误' }}
                </el-tag>
                <span v-if="question.subject" class="question-subject">{{ question.subject }}</span>
                <span v-if="question.knowledgePoint" class="question-point">{{ question.knowledgePoint }}</span>
              </div>
              <img
                v-if="question.paperCutImg"
                :src="question.paperCutImg"
                class="question-image"
                @click="viewImage(question.paperCutImg)" />
              <div v-if="question.text" class="question-text">{{ question.text }}</div>
              <div v-if="question.answer" class="question-answer">
                <span class="answer-label">参考答案：</span>{{ question.answer }}
              </div>
            </div>
          </el-checkbox-group>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 图片预览 -->
    <el-dialog
      :visible.sync="imagePreviewVisible"
      width="80%"
      center
      :show-close="false"
      @click.native="imagePreviewVisible = false">
      <img :src="previewImageUrl" style="width: 100%; max-height: 70vh; object-fit: contain;" />
    </el-dialog>
  </div>
</template>

<script>
import { axios } from '../../config/axios'
import moment from 'moment'

export default {
  name: 'HomeworkCorrection',
  data() {
    return {
      dateRange: [],
      loading: false,
      recordList: [],
      currentPage: 1,
      pageSize: 12,
      total: 0,
      detailVisible: false,
      detailLoading: false,
      currentRecord: null,
      questionFilter: 'all',
      selectedQuestionIds: [],
      imagePreviewVisible: false,
      previewImageUrl: ''
    }
  },
  computed: {
    filteredQuestions() {
      const list = (this.currentRecord && this.currentRecord.questionList) || []
      if (this.questionFilter === 'wrong') {
        return list.filter(item => !item.isCorrect)
      }
      if (this.questionFilter === 'correct') {
        return list.filter(item => item.isCorrect)
      }
      return list
    }
  },
  created() {
    this.loadRecords()
  },
  methods: {
    navigateTo(path) {
      if (this.$route.path !== path) {
        this.$router.push(path)
      }
    },
    dateParams() {
      if (this.dateRange && this.dateRange.length === 2) {
        return { startDate: this.dateRange[0], endDate: this.dateRange[1] }
      }
      return {}
    },
    async loadRecords() {
      this.loading = true
      try {
        const params = Object.assign(
          { pageNumber: this.currentPage, pageSize: this.pageSize },
          this.dateParams()
        )
        const response = await axios.get('/homeworkCorrection/list', { params })
        const res = response.data || {}
        if (res.code !== 200) {
          this.recordList = []
          this.total = 0
          this.$message.error(res.message || '加载批改记录失败')
          return
        }
        const data = res.data || {}
        this.recordList = (data.list || []).filter(item => item && item.id)
        this.total = data.total || 0
      } catch (error) {
        console.error('加载批改记录失败:', error)
        this.recordList = []
        this.total = 0
        this.$message.error('加载批改记录失败')
      } finally {
        this.loading = false
      }
    },
    async openDetail(record) {
      if (!record || !record.id) return
      this.detailVisible = true
      this.detailLoading = true
      this.currentRecord = null
      this.questionFilter = 'all'
      this.selectedQuestionIds = []
      try {
        const response = await axios.get('/homeworkCorrection/detail', { params: { id: record.id } })
        const res = response.data || {}
        if (res.code !== 200) {
          this.$message.error(res.message || '加载批改详情失败')
          this.detailVisible = false
          return
        }
        this.currentRecord = res.data || null
      } catch (error) {
        console.error('加载批改详情失败:', error)
        this.$message.error('加载批改详情失败')
        this.detailVisible = false
      } finally {
        this.detailLoading = false
      }
    },
    async removeRecord(record) {
      if (!record || !record.id) return
      try {
        await this.$confirm('删除后该次批改记录将不再展示，是否继续？', '提示', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return
      }
      try {
        const response = await axios.delete(`/homeworkCorrection/${record.id}`)
        const res = response.data || {}
        if (res.code !== 200) {
          this.$message.error(res.message || '删除失败')
          return
        }
        this.$message.success('已删除')
        this.loadRecords()
      } catch (error) {
        console.error('删除批改记录失败:', error)
        this.$message.error('删除失败')
      }
    },
    async batchPrint() {
      try {
        const response = await axios.post('/homeworkCorrection/batch-print', {
          questionIds: this.selectedQuestionIds
        }, {
          headers: { 'Content-Type': 'application/json' },
          transformRequest: [(data) => JSON.stringify(data)]
        })
        const res = response.data || {}
        if (res.code !== 200) {
          this.$message.error(res.message || '打印失败')
          return
        }
        this.$message.success('打印任务已提交')
        this.selectedQuestionIds = []
      } catch (error) {
        console.error('批量打印失败:', error)
        this.$message.error('打印失败')
      }
    },
    handleFilterChange() {
      this.currentPage = 1
      this.loadRecords()
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.loadRecords()
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.loadRecords()
    },
    recordImage(record) {
      if (!record) return ''
      return record.correctionImgUrl || record.annotatedImageUrl || record.dealImgUrl || record.orgImgUrl || ''
    },
    recordRate(record) {
      const total = (record && record.questionCount) || 0
      if (!total) return 0
      return Math.round(((record.correctCount || 0) * 100) / total)
    },
    rateColor(rate) {
      if (rate >= 85) return '#67c23a'
      if (rate >= 60) return '#e6a23c'
      return '#f56c6c'
    },
    viewImage(imageUrl) {
      if (!imageUrl) return
      this.previewImageUrl = imageUrl
      this.imagePreviewVisible = true
    },
    formatTime(time) {
      return time ? moment(time).format('YYYY-MM-DD HH:mm') : '--'
    }
  }
}
</script>

<style lang="less" scoped>
.homework-correction {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100%;
}

.page-header {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}

.header-shortcuts {
  display: flex;
  align-items: center;
  gap: 8px;
}

.shortcut-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background: #f0f5ff;
    color: #3498db;
  }
}

.header-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #909399;
}

.filter-bar {
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.record-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.record-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }
}

.record-image {
  width: 100%;
  height: 190px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  background: #f2f3f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #c0c4cc;
  font-size: 13px;

  i {
    font-size: 26px;
  }

  &.large {
    height: 320px;
    border-radius: 8px;
    background: #f2f3f5;
  }
}

.record-time {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.record-counts {
  display: flex;
  gap: 12px;
  font-size: 14px;
  margin-bottom: 10px;

  .count-total {
    color: #2c3e50;
    font-weight: 600;
  }

  .count-correct {
    color: #67c23a;
  }

  .count-wrong {
    color: #f56c6c;
  }
}

.record-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;

  .el-button {
    flex: 1;
    margin: 0;
  }
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  padding: 20px;
}

.detail-loading {
  padding: 40px;
  text-align: center;
  color: #909399;
}

.detail-body {
  display: flex;
  gap: 24px;
}

.detail-left {
  width: 45%;
  flex-shrink: 0;
}

.detail-image {
  width: 100%;
  border-radius: 8px;
  cursor: zoom-in;
}

.detail-meta {
  margin-top: 12px;
  font-size: 13px;
  color: #606266;
  line-height: 1.8;
}

.detail-right {
  flex: 1;
  min-width: 0;
}

.question-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.question-list {
  max-height: 460px;
  overflow-y: auto;
  padding-right: 4px;
}

.question-item {
  border: 1px solid #ebeef5;
  border-left: 3px solid #67c23a;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;

  &.wrong {
    border-left-color: #f56c6c;
    background: #fef6f6;
  }
}

.question-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  /deep/ .el-checkbox__label {
    display: none;
  }
}

.question-subject {
  font-size: 13px;
  color: #2c3e50;
  font-weight: 600;
}

.question-point {
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.question-image {
  width: 100%;
  border-radius: 6px;
  cursor: zoom-in;
  margin-bottom: 8px;
}

.question-text {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  margin-bottom: 6px;
}

.question-answer {
  font-size: 13px;
  color: #2c3e50;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 8px 10px;
  line-height: 1.6;
}

.answer-label {
  font-weight: 600;
}

.loading-container,
.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  background: white;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.loading-text {
  color: #7f8c8d;
  font-size: 16px;

  i {
    margin-right: 8px;
  }
}

.empty-text {
  color: #909399;
  font-size: 14px;

  &.small {
    display: block;
    padding: 40px 0;
    text-align: center;
  }
}

.text-danger {
  color: #f56c6c !important;
}

@media (max-width: 768px) {
  .homework-correction {
    padding: 15px;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .detail-body {
    flex-direction: column;
  }

  .detail-left {
    width: 100%;
  }
}
</style>
